import type { IBillItemTimerDiscount } from "@/data/model/Bill";
import { DiscountType } from "@/data/model/Enums";

// The elapsed time of all the runs of a price per minute timer. The server tells it as of the time the bill was read, and
// the device counts on from there with its own clock while the timer runs: the clock of the device does not have to
// match the one of the server
export function getTimerElapsedSeconds(
    elapsedSecondsWhenRead: number,
    readDateTime: Date,
    isRunning: boolean,
    now: Date
): number {
    if (!isRunning) return elapsedSecondsWhenRead;

    return elapsedSecondsWhenRead + Math.max(0, Math.floor((now.getTime() - readDateTime.getTime()) / 1000));
}

// The price of a session as the server prices it: the rate x the elapsed minutes rounded to cents, then rounded up to CHF
// 0.50. The rate has up to 5 decimals (28 CHF per hour is 0.46667, 60 minutes are 28.0002, which is 28.00, not 28.50). It is
// worked out in whole units of 0.00001 and seconds, so no float noise tips either rounding
export function calculateTimerPrice(minuteRate: number, elapsedSeconds: number): number {
    const rateUnits = Math.round(minuteRate * 100000);

    // rate x seconds / 60 in cents: rateUnits / 100000 x seconds / 60 x 100 = rateUnits x seconds / 60000, rounded half away
    // from zero like the server
    const units = rateUnits * elapsedSeconds;
    const wholeCents = Math.floor(Math.abs(units) / 60000);
    const remainder = Math.abs(units) - wholeCents * 60000;
    const cents = Math.sign(units) * (remainder >= 30000 ? wholeCents + 1 : wholeCents);

    // in units of 0.50
    return Math.ceil(cents / 50) / 2;
}

// The unit price of a running timer after its item discounts, as the server works them out (BillItem.GetPricePerMinuteDiscountAmount):
// in the order they were applied, each on the price left by the ones before it. A percentage is rounded to cents half away
// from zero, an absolute discount takes at most the price left. Worked out in whole cents, and a percentage in hundredths of
// a percent, so no float noise tips the rounding
export function applyTimerDiscounts(unitPrice: number, discounts: readonly IBillItemTimerDiscount[]): number {
    let cents = Math.round(unitPrice * 100);
    for (const discount of discounts) {
        let discountCents = 0;
        if (discount.discountType === DiscountType.Percentage && discount.percentage !== undefined) {
            const units = cents * Math.round(discount.percentage * 100);
            const wholeCents = Math.floor(units / 10000);
            discountCents = units - wholeCents * 10000 >= 5000 ? wholeCents + 1 : wholeCents;
        } else if (discount.discountType === DiscountType.Absolute && discount.absoluteAmount !== undefined) {
            discountCents = Math.min(cents, Math.round(discount.absoluteAmount * 100));
        }

        cents -= discountCents;
    }

    return cents / 100;
}
